'use client';

import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import { Section } from '@/components/ui/Section';
import styles from './QRLocations.module.css';

export interface QRLocation {
  name: string;
  image: string;
  alt: string;
}

export interface QRLocationsProps {
  title: string;
  subtitle: string;
  locations: QRLocation[];
  badgeText?: string; // Translation for badge
}

/**
 * QRLocations Component
 * Displays a 3x2 grid of QR ordering locations with images
 */
export function QRLocations({ title, subtitle, locations, badgeText = 'Trusted by Industry Leaders' }: QRLocationsProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = gridRef.current?.querySelectorAll(`.${styles.locationCard}`);
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.section}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.badge}>{badgeText}</div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>

          <div className={styles.grid} ref={gridRef}>
            {locations.map((location, index) => (
              <div
                key={index}
                className={styles.locationCard}
                style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
              >
                <div className={styles.imageWrapper}>
                  <Image
                    src={location.image}
                    alt={location.alt}
                    fill
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 50vw, 33vw"
                    className={styles.image}
                    loading="lazy"
                  />
                  <div className={styles.overlay}>
                    <div className={styles.locationName}>{location.name}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

